import times from 'lodash/times';

import {
  CPUPressureEventDetails,
  CPUPressureResolvedEventDetails,
  FailedHelmTestEventDetails,
  HPAScalingPolicyType,
  OOMKillEventDetails,
  RecommendedPodCountChangedEventDetails,
  RecommendedRequestsChangedEventDetails,
  ScalingPolicyAssignedEventDetails,
  ScalingPolicyCreatedEventDetails,
  ScalingPolicyDeletedEventDetails,
  ScalingPolicyOrderUpdatedEventDetails,
  ScalingPolicyUpdatedEventDetails,
  StartupFailureEventDetails,
  SurgeEventDetails,
  SystemOverrideResetEventDetails,
  SystemOverrideTriggeredEventDetails,
  WorkloadConfigChangedEventDetails,
  WorkloadEventType,
  WorkloadOptimizationEvent,
  WorkloadOptimizationEventAffectedWorkload,
  WorkloadOptimizationEventDetails,
  WorkloadOptimizationResourceConfigFunction,
  WorkloadOptimizationState,
} from '@cast/types';

const resourceConfigBase = {
  args: [],
  function: WorkloadOptimizationResourceConfigFunction.QUANTILE,
  overhead: 4,
};

type WorkloadEventParams = Partial<WorkloadOptimizationEvent> & {
  containersCount?: number;
};

const generateContainerWithResources = (index: number) => ({
  name: `container-${index}`,
  resources: {
    requests: {
      cpuCores: 100 + index * 10,
      memoryGib: 1.5 + index * 0.5,
    },
  },
});

export function workloadEventOf(
  params?: Partial<WorkloadOptimizationEvent>
): WorkloadOptimizationEvent {
  return {
    id: 'event-1',
    clusterId: 'cluster-id',
    organizationId: 'org-id',
    createdAt: '2024-02-02T18:19:00.075Z',
    type: WorkloadEventType.OOM_KILL,
    event: {},
    ...params,
  };
}

export function eventWorkloadOf(
  params?: Partial<WorkloadOptimizationEventAffectedWorkload>
): WorkloadOptimizationEventAffectedWorkload {
  return {
    id: 'workload-id-1',
    kind: 'Deployment',
    name: 'test-workload-1',
    namespace: 'e2e',
    ...params,
  };
}

export function surgeEventDetailsOf(
  params?: Partial<SurgeEventDetails> & { containersCount?: number }
): WorkloadOptimizationEventDetails {
  const { containersCount, ...detailsParams } = params ?? {};
  const count = containersCount ?? 0;
  return {
    surge: {
      containers: times(count, (index) => ({
        name: `container-${index}`,
        surge: {
          cpuCores: 150 + index * 20,
          memoryGib: 2.0 + index * 0.3,
        },
        recommendedBefore: {
          cpuCores: 100 + index * 10,
          memoryGib: 1.5 + index * 0.2,
        },
      })),
      ...detailsParams,
    },
  };
}

export function surgeEventOf(
  params?: WorkloadEventParams
): WorkloadOptimizationEvent {
  const { containersCount, ...eventParams } = params ?? {};
  return workloadEventOf({
    type: WorkloadEventType.SURGE,
    event: surgeEventDetailsOf({ containersCount }),
    ...eventParams,
  });
}

export function oomKillEventDetailsOf(
  params?: Partial<OOMKillEventDetails> & { containersCount?: number }
): WorkloadOptimizationEventDetails {
  const { containersCount, ...detailsParams } = params ?? {};
  const count = containersCount ?? 0;
  return {
    oomKill: {
      containers: times(count, generateContainerWithResources),
      ...detailsParams,
    },
  };
}

export function oomKillEventOf(
  params?: WorkloadEventParams
): WorkloadOptimizationEvent {
  const { containersCount, ...eventParams } = params ?? {};
  return workloadEventOf({
    type: WorkloadEventType.OOM_KILL,
    event: oomKillEventDetailsOf({ containersCount }),
    ...eventParams,
  });
}

export function recommendedPodCountChangedEventDetailsOf(
  params?: Partial<RecommendedPodCountChangedEventDetails>
): WorkloadOptimizationEventDetails {
  return {
    recommendedPodCountChanged: {
      previous: 5,
      current: 10,
      ...params,
    },
  };
}

export function recommendedPodCountChangedEventOf(
  params?: Partial<WorkloadOptimizationEvent>
): WorkloadOptimizationEvent {
  return workloadEventOf({
    type: WorkloadEventType.RECOMMENDED_POD_COUNT_CHANGED,
    event: recommendedPodCountChangedEventDetailsOf(),
    ...params,
  });
}

export function recommendedRequestsChangedEventDetailsOf(
  params?: Partial<RecommendedRequestsChangedEventDetails>
): WorkloadOptimizationEventDetails {
  return {
    recommendedRequestsChanged: {
      current: {
        containers: [
          {
            name: 'workload-metrics',
            resources: {
              limits: {
                memoryGib: 8.717047723010182,
              },
              requests: {
                cpuCores: 1.431,
                memoryGib: 5.811365148983896,
              },
            },
          },
        ],
      },
      previous: {
        containers: [
          {
            name: 'workload-metrics',
            resources: {
              limits: {
                memoryGib: 8.717047723010182,
              },
              requests: {
                cpuCores: 1.431,
                memoryGib: 5.811365148983896,
              },
            },
          },
        ],
      },
      applyType: 'IMMEDIATE',
      ...params,
    },
  };
}

export function recommendedRequestsChangedEventOf(
  params?: Partial<WorkloadOptimizationEvent>
): WorkloadOptimizationEvent {
  return workloadEventOf({
    type: WorkloadEventType.RECOMMENDED_REQUESTS_CHANGED,
    event: recommendedRequestsChangedEventDetailsOf(),
    ...params,
  });
}

export function configurationChangedEventDetailsOf(
  params?: Partial<WorkloadConfigChangedEventDetails>
): WorkloadOptimizationEventDetails {
  return {
    configurationChangedV2: {
      current: {
        workloadConfig: {
          hpaConfig: {
            managementOption: 'MANAGED',
            minReplicas: 1,
            maxReplicas: 10,
          },
          vpaConfig: {
            managementOption: WorkloadOptimizationState.READ_ONLY,
            containerConstraints: [
              {
                containerName: 'container-0',
                cpu: {
                  ...resourceConfigBase,
                  constraints: {
                    min: { constant: { value: 0.1 } },
                    max: { constant: { value: 1 } },
                  },
                },
                memory: {
                  ...resourceConfigBase,
                  constraints: {
                    min: { constant: { value: 100 } },
                    max: { constant: { value: 1000 } },
                  },
                },
              },
            ],
            cpu: {
              ...resourceConfigBase,
            },
            memory: {
              ...resourceConfigBase,
            },
            antiAffinity: {},
          },
        },
        scalingPolicyConfig: {
          id: 'scaling-policy-id',
          name: 'scaling-policy-name',
        },
      },
      previous: {
        workloadConfig: {
          hpaConfig: {
            managementOption: 'READ_ONLY',
            minReplicas: 5,
            maxReplicas: 15,
          },
          vpaConfig: {
            managementOption: WorkloadOptimizationState.MANAGED,
            containerConstraints: [
              {
                containerName: 'container-0',
                cpu: {
                  ...resourceConfigBase,
                  constraints: {
                    min: { constant: { value: 0.2 } },
                    max: { constant: { value: 2 } },
                  },
                },
                memory: {
                  ...resourceConfigBase,
                  constraints: {
                    min: { constant: { value: 200 } },
                    max: { constant: { value: 2000 } },
                  },
                },
              },
            ],
            cpu: {
              ...resourceConfigBase,
            },
            memory: {
              ...resourceConfigBase,
            },
            antiAffinity: {},
          },
        },
        scalingPolicyConfig: {
          id: 'scaling-policy-id',
          name: 'scaling-policy-name',
        },
      },
      ...params,
    } as unknown as WorkloadOptimizationEventDetails['configurationChangedV2'],
  };
}

export function configurationChangedEventOf(
  params?: Partial<WorkloadOptimizationEvent>
): WorkloadOptimizationEvent {
  return workloadEventOf({
    type: WorkloadEventType.CONFIGURATION_CHANGEDV2,
    event: configurationChangedEventDetailsOf(),
    ...params,
  });
}

export function scalingPolicyCreatedEventDetailsOf(
  params?: Partial<ScalingPolicyCreatedEventDetails>
): WorkloadOptimizationEventDetails {
  return {
    scalingPolicyCreated: {
      policy: {
        name: 'policy-name-created',
        applyType: 'DEFERRED',
        recommendationPolicies: {
          applyType: 'IMMEDIATE',
        },
      },
      ...params,
    } as WorkloadOptimizationEventDetails['scalingPolicyCreated'],
  };
}

export function scalingPolicyCreatedEventOf(
  params?: Partial<WorkloadOptimizationEvent>
): WorkloadOptimizationEvent {
  return workloadEventOf({
    type: WorkloadEventType.SCALING_POLICY_CREATED,
    event: scalingPolicyCreatedEventDetailsOf(),
    ...params,
  });
}

export function scalingPolicyAssignedEventDetailsOf(
  params?: Partial<ScalingPolicyAssignedEventDetails>
): WorkloadOptimizationEventDetails {
  return {
    scalingPolicyAssigned: {
      policy: {
        name: 'policy-name-assigned',
        applyType: 'DEFERRED',
        recommendationPolicies: {
          applyType: 'IMMEDIATE',
        },
      },
      ...params,
    } as WorkloadOptimizationEventDetails['scalingPolicyAssigned'],
  };
}

export function scalingPolicyAssignedEventOf(
  params?: Partial<WorkloadOptimizationEvent>
): WorkloadOptimizationEvent {
  return workloadEventOf({
    type: WorkloadEventType.SCALING_POLICY_ASSIGNED,
    event: scalingPolicyAssignedEventDetailsOf(),
    ...params,
  });
}

export function scalingPolicyDeletedEventDetailsOf(
  params?: Partial<ScalingPolicyDeletedEventDetails>
): WorkloadOptimizationEventDetails {
  return {
    scalingPolicyDeleted: {
      policy: {
        name: 'policy-name-deleted',
        applyType: 'DEFERRED',
        recommendationPolicies: {
          applyType: 'IMMEDIATE',
        },
      },
      ...params,
    } as WorkloadOptimizationEventDetails['scalingPolicyDeleted'],
  };
}

export function scalingPolicyDeletedEventOf(
  params?: Partial<WorkloadOptimizationEvent>
): WorkloadOptimizationEvent {
  return workloadEventOf({
    type: WorkloadEventType.SCALING_POLICY_DELETED,
    event: scalingPolicyDeletedEventDetailsOf(),
    ...params,
  });
}

export function scalingPolicyUpdatedEventDetailsOf(
  params?: Partial<ScalingPolicyUpdatedEventDetails>
): WorkloadOptimizationEventDetails {
  return {
    scalingPolicyUpdated: {
      previous: {
        recommendationPolicies: {
          cpu: {
            applyThreshold: 0.2,
            overhead: 0.1,
            args: ['0.3'],
          },
          memory: {
            applyThreshold: 0.25,
            overhead: 512,
            args: ['0.45'],
          },
        },
        hpaSettings: {
          managementOption: WorkloadOptimizationState.READ_ONLY,
          takeOwnership: false,
          nativeHpaSpec: {
            minReplicas: 1,
            maxReplicas: 5,
            metrics: [
              {
                type: 'RESOURCE',
                resource: {
                  name: 'cpu',
                  target: { type: 'UTILIZATION', value: '60' },
                },
              },
            ],
            behavior: {
              scaleUp: {
                stabilizationWindowSeconds: 60,
                policies: [
                  {
                    type: HPAScalingPolicyType.PODS_SCALING_POLICY,
                    value: 2,
                    periodSeconds: 60,
                  },
                ],
              },
              scaleDown: {
                stabilizationWindowSeconds: 300,
                policies: [
                  {
                    type: HPAScalingPolicyType.PERCENT_SCALING_POLICY,
                    value: 20,
                    periodSeconds: 60,
                  },
                ],
              },
            },
          },
        },
      },
      current: {
        recommendationPolicies: {
          cpu: {
            applyThreshold: 0.3,
            overhead: 0.2,
            args: ['0.4'],
          },
          memory: {
            applyThreshold: 0.35,
            overhead: 1024,
            args: ['0.55'],
          },
        },
        hpaSettings: {
          managementOption: WorkloadOptimizationState.MANAGED,
          takeOwnership: true,
          nativeHpaSpec: {
            minReplicas: 2,
            maxReplicas: 10,
            metrics: [
              {
                type: 'RESOURCE',
                resource: {
                  name: 'cpu',
                  target: { type: 'UTILIZATION', value: '70' },
                },
              },
            ],
            behavior: {
              scaleUp: {
                stabilizationWindowSeconds: 30,
                policies: [
                  {
                    type: HPAScalingPolicyType.PODS_SCALING_POLICY,
                    value: 4,
                    periodSeconds: 60,
                  },
                ],
              },
              scaleDown: {
                stabilizationWindowSeconds: 600,
                policies: [
                  {
                    type: HPAScalingPolicyType.PERCENT_SCALING_POLICY,
                    value: 10,
                    periodSeconds: 60,
                  },
                ],
              },
            },
          },
        },
      },
      ...params,
    } as WorkloadOptimizationEventDetails['scalingPolicyUpdated'],
  };
}

export function scalingPolicyUpdatedEventOf(
  params?: Partial<WorkloadOptimizationEvent>
): WorkloadOptimizationEvent {
  return workloadEventOf({
    type: WorkloadEventType.SCALING_POLICY_UPDATED,
    event: scalingPolicyUpdatedEventDetailsOf(),
    ...params,
  });
}

export function scalingPolicyOrderUpdatedEventDetailsOf(
  params?: Partial<ScalingPolicyOrderUpdatedEventDetails>
): WorkloadOptimizationEventDetails {
  return {
    scalingPolicyOrderUpdated: {
      previous: [
        { id: 'e998fd3d-d9b5-40ff-be98-d22ed93e36a7', name: 'stability' },
        { id: 'fb90f9a4-0275-4644-afee-9a1d91564983', name: 'readonly' },
        { id: '0a3c33ad-d003-4529-9cd2-5a6d559d853c', name: 'cost-savings' },
        { id: '3f71ab63-2b17-4366-ad4d-91566dc71a70', name: 'default' },
      ],
      current: [
        { id: '3f71ab63-2b17-4366-ad4d-91566dc71a70', name: 'default' },
        { id: 'fb90f9a4-0275-4644-afee-9a1d91564983', name: 'readonly' },
        { id: 'e998fd3d-d9b5-40ff-be98-d22ed93e36a7', name: 'stability' },
        { id: '0a3c33ad-d003-4529-9cd2-5a6d559d853c', name: 'cost-savings' },
      ],
      ...params,
    },
  };
}

export function scalingPolicyOrderUpdatedEventOf(
  params?: Partial<WorkloadOptimizationEvent>
): WorkloadOptimizationEvent {
  return workloadEventOf({
    type: WorkloadEventType.SCALING_POLICY_ORDER_UPDATED,
    event: scalingPolicyOrderUpdatedEventDetailsOf(),
    ...params,
  });
}

export function failedHelmTestHookEventDetailsOf(
  params?: Partial<FailedHelmTestEventDetails>
): WorkloadOptimizationEventDetails {
  return {
    failedHook: {
      message: 'test-hook-failed message',
      time: '2024-02-02T18:19:00.075Z',
      ...params,
    },
  };
}

export function failedHelmTestHookEventOf(
  params?: Partial<WorkloadOptimizationEvent>
): WorkloadOptimizationEvent {
  return workloadEventOf({
    type: WorkloadEventType.FAILED_HELM_TEST_HOOK,
    event: failedHelmTestHookEventDetailsOf(),
    ...params,
  });
}

export function systemOverrideResetEventDetailsOf(
  params?: Partial<SystemOverrideResetEventDetails>
): WorkloadOptimizationEventDetails {
  return {
    systemOverrideReset: {
      target: 'SYSTEM_OVERRIDE_TARGET_ALL',
      origin: 'SYSTEM_OVERRIDE_RESET_ORIGIN_ANNOTATIONS_OFF',
      ...params,
    },
  };
}

export function systemOverrideResetEventOf(
  params?: Partial<WorkloadOptimizationEvent>
): WorkloadOptimizationEvent {
  return workloadEventOf({
    type: WorkloadEventType.SYSTEM_OVERRIDE_RESET,
    event: systemOverrideResetEventDetailsOf(),
    ...params,
  });
}

export function systemOverrideTriggeredEventDetailsOf(
  params?: Partial<SystemOverrideTriggeredEventDetails>
): WorkloadOptimizationEventDetails {
  return {
    systemOverrideTriggered: {
      target: 'SYSTEM_OVERRIDE_TARGET_VERTICAL_OPTIMIZATION',
      origin: 'SYSTEM_OVERRIDE_ORIGIN_CONTINUOUS_OOM_KILLED',
      ...params,
    },
  };
}

export function systemOverrideTriggeredEventOf(
  params?: Partial<WorkloadOptimizationEvent>
): WorkloadOptimizationEvent {
  return workloadEventOf({
    type: WorkloadEventType.SYSTEM_OVERRIDE_TRIGGERED,
    event: systemOverrideTriggeredEventDetailsOf(),
    ...params,
  });
}

export function startupFailureEventDetailsOf(
  params?: Partial<StartupFailureEventDetails> & { containersCount?: number }
): WorkloadOptimizationEventDetails {
  const { containersCount, ...detailsParams } = params ?? {};
  const count = containersCount ?? 0;
  return {
    startupFailure: {
      containers: times(Math.max(count, 1), (index) => ({
        name: `container-${index}`,
        probeFailureIncrease: 10 + index * 2,
      })),
      ...detailsParams,
    },
  };
}

export function startupFailureEventOf(
  params?: WorkloadEventParams
): WorkloadOptimizationEvent {
  const { containersCount, ...eventParams } = params ?? {};
  return workloadEventOf({
    type: WorkloadEventType.STARTUP_FAILURE,
    event: startupFailureEventDetailsOf({ containersCount }),
    ...eventParams,
  });
}

export function cpuPressureEventDetailsOf(
  params?: Partial<CPUPressureEventDetails> & { containersCount?: number }
): WorkloadOptimizationEventDetails {
  const { containersCount, ...detailsParams } = params ?? {};
  const count = containersCount ?? 0;
  return {
    cpuPressure: {
      containers: times(count, (index) => ({
        name: `container-${index}`,
        pressurePercentage: 15.0 + index * 5.0,
        eligiblePodCount: 2 + index,
        podCountTotal: 5 + index,
      })),
      ...detailsParams,
    },
  };
}

export function cpuPressureEventOf(
  params?: WorkloadEventParams
): WorkloadOptimizationEvent {
  const { containersCount, ...eventParams } = params ?? {};
  return workloadEventOf({
    type: WorkloadEventType.CPU_PRESSURE,
    event: cpuPressureEventDetailsOf({ containersCount }),
    ...eventParams,
  });
}

export function cpuPressureResolvedEventDetailsOf(
  params?: Partial<CPUPressureResolvedEventDetails> & {
    containersCount?: number;
  }
): WorkloadOptimizationEventDetails {
  const { containersCount, ...detailsParams } = params ?? {};
  const count = containersCount ?? 0;
  const baseTime = new Date('2024-02-02T18:00:00.075Z').getTime();

  return {
    cpuPressureResolved: {
      containers: times(count, (index) => {
        // Staggered start times (5 minutes apart)
        const startOffset = index * 300000;
        // Variable duration (10-25 minutes)
        const duration = 600000 + index * 300000;

        return {
          name: `container-${index}`,
          startedAt: new Date(baseTime + startOffset).toISOString(),
          stoppedAt: new Date(baseTime + startOffset + duration).toISOString(),
        };
      }),
      ...detailsParams,
    },
  };
}

export function cpuPressureResolvedEventOf(
  params?: WorkloadEventParams
): WorkloadOptimizationEvent {
  const { containersCount, ...eventParams } = params ?? {};
  return workloadEventOf({
    type: WorkloadEventType.CPU_PRESSURE_RESOLVED,
    event: cpuPressureResolvedEventDetailsOf({ containersCount }),
    ...eventParams,
  });
}

export function hpaMaxedOutEventOf(
  params?: WorkloadEventParams
): WorkloadOptimizationEvent {
  return workloadEventOf({
    type: WorkloadEventType.HPA_MAXED_OUT,
    event: { hpaMaxedOut: { desiredReplicas: 10, maxReplicas: 10 } },
    ...params,
  });
}

export function hpaAlmostMaxedOutEventOf(
  params?: WorkloadEventParams
): WorkloadOptimizationEvent {
  return workloadEventOf({
    type: WorkloadEventType.HPA_ALMOST_MAXED_OUT,
    event: { hpaAlmostMaxedOut: { desiredReplicas: 9, maxReplicas: 10 } },
    ...params,
  });
}

export function unboundMemoryGrowthEventOf(
  params?: WorkloadEventParams
): WorkloadOptimizationEvent {
  return workloadEventOf({
    type: WorkloadEventType.UNBOUND_MEMORY_GROWTH,
    event: { unboundMemoryGrowth: {} },
    ...params,
  });
}

export function nativeVpaStateChangedEventOf(
  params?: WorkloadEventParams
): WorkloadOptimizationEvent {
  return workloadEventOf({
    type: WorkloadEventType.NATIVE_VPA_STATE_CHANGED,
    event: { nativeVpaStateChanged: { vpaPresent: true } },
    ...params,
  });
}

export function invalidEventOf(
  params?: WorkloadEventParams
): WorkloadOptimizationEvent {
  return workloadEventOf({
    type: WorkloadEventType.INVALID,
    event: {},
    ...params,
  });
}
